import React from "react";
import { Clock, User, Plus } from "lucide-react";
import FormInput from "./FormInput";
import Button from "./Button";
import { InlineLoader as Loader } from "./Loader";

/**
 * Reusable Interaction History / Activity Timeline
 */
const InteractionHistory = ({ 
  activities = [], 
  loading = false, 
  note = "", 
  setNote, 
  onAddActivity, 
  isSubmitting = false,
  title = "Interaction History",
  height = "h-[600px]",
  filterById = null,
  idField = "leadId"
}) => {
  // Normalize and filter activities
  const displayActivities = activities
    .filter(a => !filterById || a[idField] === filterById)
    .sort((a, b) => {
      const dateA = new Date(a.createdAt || a.date || 0);
      const dateB = new Date(b.createdAt || b.date || 0);
      return dateB - dateA;
    });

  return (
    <div className={`flex flex-col ${height}`}>
      <h4 className="font-bold text-sm text-brand-dark border-b border-slate-200 pb-3 mb-4">
        {title}
      </h4>

      {/* Add Note */}
      {onAddActivity && (
        <div className="flex items-end gap-2 mb-4">
          <FormInput
            className="flex-1"
            inputClassName="!rounded-md shadow-sm border-slate-200"
            size="md"
            value={note}
            onChange={e => setNote && setNote(e.target.value)}
            placeholder="Add activity note..."
            onKeyDown={e => e.key === 'Enter' && onAddActivity()}
          />
          <Button 
            variant="orange" 
            className="!rounded-md shadow-md h-[35px] w-[45px] p-0 flex items-center justify-center shrink-0"
            onClick={onAddActivity}
            loading={isSubmitting}
          >
            <Plus size={20} />
          </Button>
        </div>
      )}

      {/* Timeline */}
      <div className="flex-1 overflow-y-auto pr-1 space-y-3 custom-scrollbar">
        {loading ? (
          <div className="flex justify-center py-10"><Loader /></div>
        ) : displayActivities.length > 0 ? (
          displayActivities.map((act, i) => {
            const date = new Date(act.createdAt || act.date);
            const authorName = act.performedBy?.name || act.userName || act.user || "System";
            const authorRole = act.performedBy?.role || act.userRole || "";
            const content = act.comment || act.note || act.message || "";

            return (
              <div key={act._id || i} className={`p-3 rounded-lg border ${i === 0 ? "bg-white border-brand-blue/20 shadow-sm" : "bg-white/50 border-slate-100"}`}>
                <div className="flex flex-col justify-between mb-1.5">
                  <span className="text-[11px] font-bold text-brand-dark flex items-center gap-1.5 flex-wrap">
                    <User size={10} className="text-slate-400" />
                    {authorName}
                    {authorRole && (
                      <span className="text-[9px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded uppercase font-medium">
                        {authorRole.replace(/[-_]/g, ' ')}
                      </span>
                    )}
                  </span>
                  <span className="text-[9px] text-slate-400 font-bold uppercase">
                    {date.toLocaleDateString("en-IN", { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </span>
                <div className="text-[11px] text-slate-600 leading-relaxed whitespace-pre-wrap">
                  {content}
                </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-12 bg-white/40 rounded-xl border border-dashed border-slate-200">
            <div className="text-2xl mb-2 opacity-50">📋</div>
            <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">No interactions recorded</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InteractionHistory;
